import { authenticatedFetch } from './apiClient.js';

// Module-level lock so two simultaneous speakWithElevenLabs() calls
// can't end up playing two audio elements at the same time. Any new
// call interrupts whatever is currently playing.
let currentGlobalAudio = null;
let isSpeakingGlobal = false;

// Persistent AudioContext + silent oscillator for iOS. Created lazily
// inside a user gesture (call keepAudioSessionAlive from a button
// handler) and reused across every speakWithElevenLabs invocation in
// the same conversation. Without persistence, the session goes cold
// between turns — iOS rejects the second guide reply because no new
// gesture is available to re-resume the context.
let persistentAudioContext = null;
let persistentOscillator = null;

export async function keepAudioSessionAlive() {
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (!isIOS) return;

  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;

    if (!persistentAudioContext) {
      persistentAudioContext = new AC();
    }

    // No-op if truly running; recovers iOS-suspended sessions otherwise.
    if (persistentAudioContext.state !== 'running') {
      await persistentAudioContext.resume();
    }

    // ALWAYS tear down and rebuild the silent oscillator. After
    // multiple Whisper getUserMedia cycles iOS invalidates the audio
    // session even when the JS-side oscillator still reports healthy
    // — and the JS heuristic was letting us skip the rebuild. Sine
    // wave at 0.00001 gain is essentially free, so recreating on
    // every call is the bulletproof move.
    if (persistentOscillator) {
      try {
        persistentOscillator.stop();
      } catch {}
      try {
        persistentOscillator.disconnect();
      } catch {}
      persistentOscillator = null;
    }

    persistentOscillator = persistentAudioContext.createOscillator();
    const gain = persistentAudioContext.createGain();
    gain.gain.value = 0.00001;
    persistentOscillator.connect(gain);
    gain.connect(persistentAudioContext.destination);
    persistentOscillator.start();
    console.log('[ElevenLabs] Oscillator (re)started');
  } catch (e) {
    console.warn('[ElevenLabs] keepAudioSessionAlive failed:', e?.message);
  }
}

// Diagnostic-only — returns the current persistent AudioContext
// state, or 'no-context' when one hasn't been created yet. Used for
// in-conversation logging from DayDetail without exporting the
// internal variable directly.
export function getAudioSessionState() {
  return persistentAudioContext?.state || 'no-context';
}

export function releaseAudioSession() {
  if (persistentOscillator) {
    try {
      persistentOscillator.stop();
    } catch {}
    persistentOscillator = null;
  }
  if (persistentAudioContext) {
    try {
      persistentAudioContext.close();
    } catch {}
    persistentAudioContext = null;
  }
  console.log('[ElevenLabs] Audio session released');
}

export function stopAllAudio() {
  if (currentGlobalAudio) {
    try {
      currentGlobalAudio.pause();
    } catch {}
    currentGlobalAudio = null;
  }
  isSpeakingGlobal = false;
}

export async function speakWithElevenLabs(text, gender = 'female', onEnd) {
  if (currentGlobalAudio) {
    try {
      currentGlobalAudio.pause();
    } catch {}
    currentGlobalAudio = null;
  }

  isSpeakingGlobal = true;

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  try {
    console.log(
      '[ElevenLabs] Voice selected:',
      gender,
      '(',
      gender === 'male' ? 'Archer' : 'Jess',
      ')'
    );

    // Make sure the iOS session is alive. First call (from a gesture)
    // creates and resumes the persistent context; subsequent calls are
    // a no-op when the context is already running. After resume(), iOS
    // can take a few extra ticks to actually transition state to
    // 'running' — poll briefly so we don't kick off the fetch on a
    // half-initialised context.
    if (isIOS) {
      await keepAudioSessionAlive();
      if (persistentAudioContext) {
        let attempts = 0;
        while (
          persistentAudioContext.state !== 'running' &&
          attempts < 10
        ) {
          await new Promise((r) => setTimeout(r, 100));
          attempts += 1;
        }
        console.log(
          '[ElevenLabs] Context state:',
          persistentAudioContext.state
        );
      }
    }

    const response = await authenticatedFetch('/api/speak', {
      method: 'POST',
      body: JSON.stringify({ text, voiceId: gender }),
    });

    if (!response.ok) {
      console.error('[ElevenLabs] Speak API error:', response.status);
      isSpeakingGlobal = false;
      if (onEnd) onEnd();
      return null;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const audio = new Audio();
    audio.playsInline = true;
    audio.src = url;
    currentGlobalAudio = audio;

    // Word-count-based fallback so a stuck onended doesn't strand the
    // promise. iOS sometimes doesn't fire onended for long utterances.
    const words = text.split(' ').length;
    const fallbackMs = words * 300 + 12000;

    const fallback = setTimeout(() => {
      console.log('[ElevenLabs] Fallback fired');
      URL.revokeObjectURL(url);
      currentGlobalAudio = null;
      isSpeakingGlobal = false;
      if (onEnd) onEnd();
    }, fallbackMs);

    audio.addEventListener('ended', () => {
      console.log('[ElevenLabs] ended fired naturally');
      clearTimeout(fallback);
      URL.revokeObjectURL(url);
      currentGlobalAudio = null;
      isSpeakingGlobal = false;
      if (onEnd) onEnd();
    });

    audio.onerror = (e) => {
      console.warn('[ElevenLabs] Audio error:', e);
      clearTimeout(fallback);
      URL.revokeObjectURL(url);
      currentGlobalAudio = null;
      isSpeakingGlobal = false;
      if (onEnd) onEnd();
    };

    console.log('[ElevenLabs] Starting playback');
    await audio.play();
    console.log('[ElevenLabs] play() succeeded');
    return audio;
  } catch (error) {
    console.error('[ElevenLabs] Error:', error?.message);
    currentGlobalAudio = null;
    isSpeakingGlobal = false;
    if (onEnd) onEnd();
    return null;
  }
}

// Plays a check-in utterance through an audio element that the caller
// already gesture-unlocked (e.g. by playing a silent MP3 inside the
// "Done & Check-in" tap). Reusing one element across the whole
// conversation works around iOS's "fresh element needs fresh gesture"
// rule — once an element has had a play() inside a gesture, swapping
// .src and calling play() again is allowed without a new gesture.
//
// Routes through /api/speak (server-side proxy with the ElevenLabs
// secret key) — never call api.elevenlabs.io directly from the
// browser, that would leak the key to anyone scraping the bundle.
export async function speakIntoElement(
  text,
  gender = 'female',
  audioElement,
  onEnd
) {
  if (!audioElement) {
    console.warn('[ElevenLabs] speakIntoElement called with null element');
    if (onEnd) onEnd();
    return null;
  }

  console.log('[speakIntoElement] entered', {
    audioElementId: audioElement && audioElement.__rootedId,
    audioElementIsNull: audioElement === null,
  });

  // Defensive: if the caller attached a primePromise to the element
  // (the Intake screen does this when it gesture-unlocks the persistent
  // <audio>), wait for it to settle before mutating .src/.load()/.play().
  // Aborting the prime mid-flight burns the iOS gesture token and
  // silently mutes the real playback.
  if (audioElement.primePromise) {
    try {
      await audioElement.primePromise;
    } catch {}
  }

  try {
    console.log(
      '[ElevenLabs] speakIntoElement Voice:',
      gender,
      '(',
      gender === 'male' ? 'Archer' : 'Jess',
      ')'
    );

    const response = await authenticatedFetch('/api/speak', {
      method: 'POST',
      body: JSON.stringify({ text, voiceId: gender }),
    });

    if (!response.ok) {
      console.warn(
        '[ElevenLabs] speakIntoElement fetch failed:',
        response.status
      );
      if (onEnd) onEnd();
      return null;
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const cleanup = () => {
      audioElement.removeEventListener('ended', handleEnded);
      audioElement.removeEventListener('error', handleError);
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {}
    };
    const handleEnded = () => {
      console.log('[ElevenLabs] speakIntoElement ended fired');
      cleanup();
      if (onEnd) onEnd();
    };
    const handleError = () => {
      console.warn('[ElevenLabs] speakIntoElement element error event');
      cleanup();
      if (onEnd) onEnd();
    };

    audioElement.addEventListener('ended', handleEnded);
    audioElement.addEventListener('error', handleError);

    audioElement.muted = false;
    audioElement.src = blobUrl;
    audioElement.load();

    try {
      await audioElement.play();
      console.log('[ElevenLabs] speakIntoElement play() succeeded');
    } catch (e) {
      console.warn(
        '[ElevenLabs] speakIntoElement play() rejected:',
        e?.message
      );
      cleanup();
      if (onEnd) onEnd();
      return null;
    }

    return audioElement;
  } catch (e) {
    console.warn('[ElevenLabs] speakIntoElement error:', e?.message);
    if (onEnd) onEnd();
    return null;
  }
}

export function stopElevenLabsAudio(audio) {
  if (audio) {
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {}
  }
  if (currentGlobalAudio === audio) {
    currentGlobalAudio = null;
  }
  isSpeakingGlobal = false;
}
